import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AuthService from "../services/auth.service";
import api from "../services/api";
import TopBar from "../components/TopBar";
import {
  useCalfForm,
  CalfIdentityFields,
  FIELD,
  LABEL,
} from "../components/forms/calfFields";

/**
 * Creating a calf asks for its identity and nothing else.
 *
 * Everything else — naissance, colostrum, allaitement, suivi — is filled in from
 * the calf's own sheet, whenever the information is actually to hand. That was
 * the point of the feedback: you should not need every answer ready in advance
 * just to register an animal.
 */
function NewCalf() {
  const navigate = useNavigate();
  const form = useCalfForm();
  const [isSaving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!AuthService.getCurrentUser()) {
      navigate("/login");
    }
  }, [navigate]);

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!form.values.bdta.trim() || !form.values.name.trim()) {
      setError("Le numéro BDTA et le nom sont nécessaires pour créer le veau.");
      return;
    }

    setSaving(true);
    setError(null);

    api
      .post("/calfs", form.toPayload())
      .then((response) => navigate("/veaux/" + response.data.pk_calf))
      .catch((err) => {
        console.error("There was an error creating the calf!", err);
        setError("La création a échoué. Réessayez.");
        setSaving(false);
      });
  };

  return (
    <div className="flex flex-col">
      <TopBar link="/"></TopBar>
      <div className="w-full self-center bg-white px-3 md:max-w-xl dark:border-slate-600 dark:bg-slate-900">
        <div className="mb-4 flex flex-col">
          <span className="text-2xl font-bold dark:text-slate-300">Ajouter un veau</span>
          <p className="text-sm text-stone-400 dark:text-slate-300">
            Le numéro BDTA et le nom suffisent. Vous compléterez la naissance, le
            colostrum et l'allaitement depuis la fiche du veau, quand vous aurez
            les informations.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="flex w-full flex-col">
          <CalfIdentityFields form={form} />

          <label htmlFor="birthdate_input" className={LABEL}>
            Date de naissance
          </label>
          <input
            className={FIELD}
            type="date"
            value={form.values.birthdate}
            onChange={(e) => form.set("birthdate")(e.target.value)}
            name="birthdate_input"
          />

          {error && (
            <p className="mb-3 text-sm text-red-600 dark:text-red-400">{error}</p>
          )}

          <button
            type="submit"
            disabled={isSaving}
            className="my-3 w-full rounded-lg bg-amber-400 p-3 font-medium text-white disabled:opacity-50 dark:bg-amber-600"
          >
            {isSaving ? "Création…" : "Créer le veau"}
          </button>
          <button
            type="button"
            onClick={() => navigate("/")}
            className="mb-3 w-full rounded-lg border border-stone-300 p-3 text-sm text-stone-500 hover:text-amber-500 dark:border-slate-700 dark:text-slate-300"
          >
            Annuler
          </button>
        </form>
      </div>
    </div>
  );
}

export default NewCalf;
